import { FC, MouseEventHandler, ReactNode, useState } from 'react';
import { graphql, useFragment } from 'react-relay';
import { FormattedMessage } from 'dibs-react-intl';
import classnames from 'classnames';
import { Link } from 'dibs-elements/exports/Link';
import { useBuyerIncentivesMessages } from 'dibs-buyer-incentives/exports/useBuyerIncentivesMessages';
import { trackEvent, eventNameConstants } from 'dibs-tracking';

import { PdpBuyerIncentivesModal } from './PdpBuyerIncentivesModal';
import { useIsMwRemovePromiseDrawerVariant } from '../../hooks/useMwRemovePromiseDrawerVariant';

import dibsCss from 'dibs-css';

import { PdpSharedSidebarBuyerIncentives_user$key } from './__generated__/PdpSharedSidebarBuyerIncentives_user.graphql';

const userFragment = graphql`
    fragment PdpSharedSidebarBuyerIncentives_user on User {
        ...PdpBuyerIncentivesModal_user
    }
`;

const Incentives: FC<{ showLink: boolean }> = ({ showLink }) => {
    const incentives = useBuyerIncentivesMessages();
    return (
        <>
            {incentives.map((incentive, index) => {
                let position = 'other';

                if (index === incentives.length - 1) {
                    position = showLink ? 'lastWithLink' : 'last';
                }

                return (
                    <div
                        key={index}
                        className={classnames(dibsCss.inlineBlock, dibsCss.whitespacePreWrap)}
                    >
                        <FormattedMessage
                            id="PdpSharedSidebarBuyerIncentives.incentive"
                            defaultMessage="{position, select, last {{incentive}} lastWithLink {{incentive} } other {{incentive}, }}"
                            values={{ incentive, position }}
                        />
                    </div>
                );
            })}
        </>
    );
};

const PdpSharedSidebarBuyerIncentives: FC<{
    user?: PdpSharedSidebarBuyerIncentives_user$key | null;
    onDetailsClick?: MouseEventHandler;
}> = ({ user: userRef, onDetailsClick }) => {
    const user = useFragment(userFragment, userRef);

    const showLink = !!onDetailsClick;
    const [isOpen, setIsOpen] = useState(false);
    const isMwRemovePromiseDrawerVariant = useIsMwRemovePromiseDrawerVariant();

    const getLink = (chunk: ReactNode): ReactNode => {
        if (isMwRemovePromiseDrawerVariant) {
            return (
                <Link
                    onClick={() => {
                        trackEvent({
                            eventName: eventNameConstants.EVENT_PRODUCT_INTERACTION,
                            interaction_type: '1stdibs promise', // add as constant in dibs tracking if test passes https://1stdibs.atlassian.net/browse/TOF-2192
                            step_interaction_name: '1stdibs promise modal displayed',
                        });
                        setIsOpen(true);
                    }}
                >
                    {chunk}
                </Link>
            );
        } else if (showLink) {
            return <Link onClick={onDetailsClick}>{chunk}</Link>;
        } else {
            return null;
        }
    };

    return (
        <>
            {isMwRemovePromiseDrawerVariant && (
                <PdpBuyerIncentivesModal
                    user={user}
                    isOpen={isOpen}
                    onClose={() => {
                        trackEvent({
                            eventName: eventNameConstants.EVENT_PRODUCT_INTERACTION,
                            interaction_type: '1stdibs promise', // add as constant in dibs tracking if test passes https://1stdibs.atlassian.net/browse/TOF-2192
                            step_interaction_name: '1stdibs promise modal closed',
                        });
                        setIsOpen(false);
                    }}
                />
            )}
            <span className={isMwRemovePromiseDrawerVariant ? dibsCss.sassyFontBodySmall : ''}>
                <FormattedMessage
                    id="PdpSharedSidebarBuyerIncentives.incentives.copy"
                    defaultMessage="<strong>The 1stDibs Promise:</strong> {incentives} <link>Details</link>"
                    values={{
                        strong: chunk => <strong>{chunk}</strong>,
                        incentives: <Incentives showLink={showLink} />,
                        link: chunk => getLink(chunk),
                    }}
                />
            </span>
        </>
    );
};

export default PdpSharedSidebarBuyerIncentives;
